import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle, Copy, Door, DotsNine, Drop, Elevator, Hammer, Info, Key, Lightbulb, LockKey, PersonSimpleWalk, Prohibit, ShieldCheck, Shower, SpeakerHigh, SpinnerGap, Star, ThermometerCold, ThermometerHot, Wrench } from "@phosphor-icons/react";
import { Header, MobileNav } from "@/components/Header";
import PhotoUploader from "@/components/PhotoUploader";
import PhoneText from "@/components/PhoneText";
import api, { formatApiError } from "@/lib/api";
import { CATEGORIES, LOCATIONS, categoryByKey } from "@/lib/constants";
import { useBuilding } from "@/context/BuildingContext";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/sonner";

const STEPS = ["Problème", "Lieu", "Détails", "Résumé"];
const AUTO_LOCATION = { ascenseur: "Ascenseur", porte_garage: "Parking", chauffage: "Chauffage", serrurerie: "Serrurerie", eclairage: "Éclairage", fuite: "Plomberie" };
const ISSUES_BY_CATEGORY = {
  ascenseur: [
    { label: "Portes bloquées", text: "Bonjour, je vous informe que les portes de l'ascenseur sont bloquées : elles ne s'ouvrent ou ne se ferment plus. Pourriez-vous intervenir rapidement ? Merci d'avance.", Icon: Door },
    { label: "Cabine coincée", text: "Bonjour, je vous informe que la cabine de l'ascenseur est coincée et ne bouge plus. Pourriez-vous intervenir rapidement ? Merci d'avance.", Icon: Elevator },
    { label: "Boutons inactifs", text: "Bonjour, je vous informe que les boutons de l'ascenseur ne répondent plus (appels ou étages). Pourriez-vous intervenir ? Merci d'avance.", Icon: DotsNine },
    { label: "Bruit anormal", text: "Bonjour, je vous informe que l'ascenseur émet un bruit anormal (grincements, à-coups). Pourriez-vous prévoir un contrôle ? Merci d'avance.", Icon: SpeakerHigh },
  ],
  fuite: [
    { label: "Fuite d'eau", text: "Bonjour, je vous informe qu'il y a une fuite d'eau (goutte-à-goutte ou écoulement visible). Pourriez-vous intervenir rapidement ? Merci d'avance.", Icon: Drop },
    { label: "Plus d'eau chaude", text: "Bonjour, je vous informe qu'il n'y a plus d'eau chaude au robinet ou à la douche. Pourriez-vous intervenir ? Merci d'avance.", Icon: Shower },
    { label: "WC bouchés", text: "Bonjour, je vous informe que les WC sont bouchés malgré mes tentatives de débouchage. Pourriez-vous intervenir ? Merci d'avance.", Icon: Prohibit },
    { label: "Robinet cassé", text: "Bonjour, je vous informe qu'un robinet est cassé ou ne fonctionne plus correctement. Pourriez-vous prévoir une réparation ? Merci d'avance.", Icon: Wrench },
  ],
  porte_garage: [
    {
      label: "Télécommande perdue ou volée",
      text: "Bonjour, je vous informe que j'ai perdu ma télécommande de la porte de garage (ou qu'elle m'a été volée). Pourriez-vous m'indiquer la marche à suivre pour la désactiver et en obtenir une nouvelle ? Merci d'avance.",
      Icon: Key,
    },
    { label: "Porte bloquée", text: "Bonjour, je vous informe que la porte de garage est bloquée : impossible de l'ouvrir ou de la fermer normalement. Pourriez-vous intervenir rapidement ? Merci d'avance.", Icon: Prohibit },
    { label: "Bruit anormal", text: "Bonjour, je vous informe que la porte de garage émet un bruit anormal lors de son ouverture ou de sa fermeture. Pourriez-vous prévoir un contrôle ? Merci d'avance.", Icon: SpeakerHigh },
    { label: "Porte endommagée", text: "Bonjour, je vous informe que la porte de garage est endommagée (choc ou détérioration visible). Pourriez-vous prévoir une réparation ? Merci d'avance.", Icon: Hammer },
    { label: "Commande d'une télécommande", text: "Bonjour, je vous informe que je souhaiterais commander une télécommande supplémentaire pour la porte de garage. Pourriez-vous m'indiquer la marche à suivre ? Merci d'avance.", Icon: Key },
  ],
  chauffage: [
    { label: "Plus de chauffage", text: "Bonjour, je vous informe qu'il n'y a plus de chauffage : les radiateurs restent froids. Pourriez-vous intervenir rapidement ? Merci d'avance.", Icon: ThermometerCold },
    { label: "Pas d'eau chaude", text: "Bonjour, je vous informe qu'il n'y a plus d'eau chaude. Pourriez-vous vérifier la chaudière ? Merci d'avance.", Icon: Drop },
    { label: "Radiateur qui fuit", text: "Bonjour, je vous informe qu'un radiateur fuit (goutte-à-goutte ou flaque d'eau). Pourriez-vous intervenir rapidement ? Merci d'avance.", Icon: Drop },
    { label: "Bruit anormal", text: "Bonjour, je vous informe que l'installation de chauffage émet un bruit anormal (radiateurs ou tuyaux). Pourriez-vous prévoir un contrôle ? Merci d'avance.", Icon: SpeakerHigh },
    { label: "Chauffage trop fort", text: "Bonjour, je vous informe que le chauffage est beaucoup trop fort et impossible à régler. Pourriez-vous vérifier ? Merci d'avance.", Icon: ThermometerHot },
  ],
  serrurerie: [
    { label: "Porte claquée", text: "Bonjour, je vous informe que la porte s'est claquée et qu'il est impossible de l'ouvrir sans clé. Pourriez-vous intervenir rapidement ? Merci d'avance.", Icon: Door },
    { label: "Clé cassée", text: "Bonjour, je vous informe qu'une clé s'est cassée dans la serrure. Pourriez-vous intervenir ? Merci d'avance.", Icon: Key },
    { label: "Serrure bloquée", text: "Bonjour, je vous informe que la serrure est bloquée : impossible d'ouvrir ou de fermer la porte. Pourriez-vous intervenir rapidement ? Merci d'avance.", Icon: LockKey },
    { label: "Demande de clé de l'immeuble", text: "Bonjour, je vous informe que j'aurais besoin d'une clé supplémentaire de l'immeuble. Pourriez-vous m'indiquer la marche à suivre pour l'obtenir ? Merci d'avance.", Icon: Key, noProvider: true },
  ],
  eclairage: [
    { label: "Ampoule grillée", text: "Bonjour, je vous informe qu'une ampoule est grillée dans les parties communes. Pourriez-vous prévoir son remplacement ? Merci d'avance.", Icon: Lightbulb },
    { label: "Détecteur HS", text: "Bonjour, je vous informe que le détecteur de présence ne fonctionne plus : la lumière ne s'allume plus automatiquement. Pourriez-vous prévoir une réparation ? Merci d'avance.", Icon: PersonSimpleWalk },
  ],
};
const ProviderRating = ({ service, testid }) =>
  (service.rating_count || 0) > 0 ? (
    <p className="mt-0.5 flex items-center gap-1.5 text-xs" data-testid={testid}>
      <span className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star key={n} size={12} weight={n <= Math.round(service.rating_avg || 0) ? "fill" : "regular"} className={n <= Math.round(service.rating_avg || 0) ? "text-[#E8A33D]" : "text-black/20"} />
        ))}
      </span>
      <span className="font-bold text-foreground/70">{String(service.rating_avg).replace(".", ",")}/5 · {service.rating_count} avis</span>
    </p>
  ) : null;

export default function ReportNew() {
  const navigate = useNavigate();
  const building = useBuilding();
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [created, setCreated] = useState(null);
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [form, setForm] = useState({
    category: null,
    location: null,
    location_detail: "",
    sub_building: "",
    garage_door: "",
    provider_id: "",
    issue: "",
    urgent: null,
    description: "",
    contact_civility: "",
    contact_first_name: "",
    contact_last_name: "",
    contact_phone: "",
    contact_email: "",
    notify_resolved: true,
    photos: [],
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const orderedCategories = useMemo(() => {
    const hidden = building.hidden_categories || [];
    const custom = building.category_custom || {};
    const visible = CATEGORIES.filter((c) => !hidden.includes(c.key)).map((c) => ({
      ...c,
      label: custom[c.key]?.label || c.label,
    }));
    const order = (building.category_order || []).filter((k) => visible.some((c) => c.key === k));
    if (!order.length) return visible;
    const sorted = order.map((k) => visible.find((c) => c.key === k));
    visible.forEach((c) => { if (!order.includes(c.key)) sorted.push(c); });
    return sorted;
  }, [building.category_order, building.hidden_categories, building.category_custom]);
  const categoryMessage = form.category ? (building.category_custom || {})[form.category]?.message : null;
  const renderCategoryNotice = () =>
    categoryMessage ? (
      <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-primary/15 bg-primary/[0.04] px-4 py-3" data-testid="report-category-message">
        <Info size={18} weight="duotone" className="mt-0.5 shrink-0 text-primary" />
        <p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground"><PhoneText text={categoryMessage} /></p>
      </div>
    ) : null;
  const isElevator = form.category === "ascenseur";
  const skipLocation = form.category in AUTO_LOCATION;
  const pickCategory = (key) =>
    setForm((f) => ({
      ...f,
      category: key,
      location: AUTO_LOCATION[key] || (AUTO_LOCATION[f.category] ? null : f.location),
      location_detail:
        (key !== "ascenseur" && f.category === "ascenseur") || (key !== "porte_garage" && f.category === "porte_garage")
          ? ""
          : f.location_detail,
      garage_door: key === "porte_garage" ? f.garage_door : "",
      sub_building: key === "porte_garage" ? "" : f.sub_building,
      provider_id: "",
      issue: "",
      urgent: null,
    }));
  const stepLabels = skipLocation ? ["Problème", isElevator ? "Panne" : "Détails", "Résumé"] : STEPS;
  const pickSubBuilding = (name) => {
    const rows = building.sub_buildings || [];
    const row = rows.find((s) => (typeof s === "string" ? s : s.name) === name);
    const preset = row && typeof row !== "string" ? row : {};
    setForm((f) => ({
      ...f,
      sub_building: name,
      location_detail:
        f.category === "ascenseur" && preset.elevator
          ? preset.elevator
          : f.location_detail,
    }));
  };
  const displayStep = skipLocation && step >= 2 ? step - 1 : step;
  const goNext = () => setStep(skipLocation && step === 0 ? 2 : step + 1);
  const goBack = () => setStep(skipLocation && step === 2 ? 0 : step - 1);
  const subRows = (building.sub_buildings || []).map((sb) => (typeof sb === "string" ? { name: sb, elevator: null } : sb));
  const selectedSub = subRows.find((s) => s.name === form.sub_building);
  const hasChauffagiste = subRows.some((sb) => (sb.chauffagiste || "").trim());
  const elevatorUnavailable = subRows.length > 0 && !subRows.some((sb) => (sb.elevator || "").trim());
  const subOptions = subRows.filter((sb) => form.category !== "ascenseur" || (sb.elevator || "").trim());
  const doorRows = (building.garage_doors || []).map((d) => (typeof d === "string" ? { name: d, number: null } : d));
  const selectedDoor = doorRows.find((d) => d.name === form.garage_door);
  const issueChips = ISSUES_BY_CATEGORY[form.category];
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data || [])).catch(() => {});
  }, [building.id]);
  const providerSpecialty = { serrurerie: "serrurerie", eclairage: "electricite", fuite: "plomberie" }[form.category];
  const providerOptions = providerSpecialty ? services.filter((s) => (s.specialites || []).includes(providerSpecialty)) : [];
  const selectedProvider = providerOptions.find((s) => s.id === form.provider_id);
  const urgentOn = form.urgent ?? (form.category === "fuite" || form.issue === "Cabine coincée");
  const urgencyEnabled = building?.features?.urgency !== false;
  const displayOf = (s) => (s.phone ? `${s.name} — ${s.phone}` : s.name);
  const ratedOf = (preset) => services.find((s) => displayOf(s) === (preset || "").trim());
  const pickIssue = (issue) => setForm((f) => ({ ...f, issue: f.issue === issue.label ? "" : issue.label, urgent: null }));
  const selectedIssue = (ISSUES_BY_CATEGORY[form.category] || []).find((i) => i.label === form.issue);
  const noProvider = !!selectedIssue?.noProvider;
  const pickDoor = (name) => {
    const door = doorRows.find((d) => d.name === name);
    setForm((f) => ({
      ...f,
      garage_door: name,
      location_detail: door ? (door.number ? `${door.name} — ${door.number}` : door.name) : "",
    }));
  };

  const canNext =
    (step === 0 && form.category) ||
    (step === 1 && form.location) ||
    (step === 2 &&
      form.description.trim().length >= 10 &&
      form.contact_civility &&
      form.contact_first_name.trim() &&
      form.contact_last_name.trim() &&
      form.contact_phone.trim() &&
      (!isElevator || form.location_detail.trim()) &&
      (!building.sub_buildings?.length || form.category === "porte_garage" || form.sub_building) &&
      (form.category !== "porte_garage" || !doorRows.length || form.garage_door) &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim())) ||
    step === 3;

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports", {
        category: form.category,
        location: form.location,
        location_detail: form.location_detail || null,
        sub_building: form.sub_building || null,
        garage_door: form.garage_door || null,
        provider_id: !noProvider && form.provider_id ? form.provider_id : null,
        issue: form.issue || null,
        urgent: form.urgent,
        description: form.description,
        contact_civility: form.contact_civility || null,
        contact_first_name: form.contact_first_name || null,
        contact_last_name: form.contact_last_name || null,
        contact_phone: form.contact_phone || null,
        contact_email: form.contact_email || null,
        notify_resolved: form.notify_resolved,
        photos: form.photos,
      });
      setPending({ id: data.pending_id });
      setCode("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/reports/confirm", { pending_id: pending.id, code: code.trim() });
      setCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setCode("");
    }
    setConfirming(false);
  };

  const copyRef = () => {
    navigator.clipboard?.writeText(created.ref);
    toast.success("Référence copiée");
  };

  if (pending && !created) {
    return (
      <div className="min-h-screen pb-24 md:pb-0" data-testid="report-verify-page">
        <Header />
        <main className="mx-auto max-w-lg px-5 py-16">
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Validez votre demande</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Un code à 6 chiffres vient d'être envoyé à <span className="font-bold text-foreground">{form.contact_email}</span>. Saisissez-le pour confirmer votre signalement.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="••••••"
            data-testid="verification-code-input"
            className="mt-8 w-full rounded-2xl border border-black/10 bg-white px-5 py-4 text-center font-mono text-2xl font-bold tracking-[0.5em] outline-none transition-colors focus:border-primary"
          />
          <button
            onClick={confirm}
            disabled={code.length !== 6 || confirming}
            data-testid="verification-submit-button"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
          >
            {confirming && <SpinnerGap size={16} className="animate-spin" />}
            Valider mon signalement
          </button>
          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              onClick={() => setPending(null)}
              data-testid="verification-back-button"
              className="font-bold text-muted-foreground transition-colors hover:text-primary"
            >
              Modifier mes informations
            </button>
            <button
              onClick={submit}
              disabled={sending}
              data-testid="verification-resend-button"
              className="font-bold text-primary transition-colors hover:underline disabled:opacity-60"
            >
              Renvoyer le code
            </button>
          </div>
        </main>
        <MobileNav />
      </div>
    );
  }

  if (created) {
    return (
      <div className="min-h-screen pb-24 md:pb-0" data-testid="report-success-page">
        <Header />
        <main className="mx-auto max-w-lg px-5 py-16 text-center">
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 16 }}>
            <CheckCircle size={72} weight="fill" className="mx-auto text-[#007A4D]" />
          </motion.div>
          <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight">C'est envoyé !</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Le gestionnaire a été informé. Conservez cette référence pour suivre l'avancement de votre demande.
          </p>
          <button
            onClick={copyRef}
            data-testid="copy-ref-button"
            className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-6 py-4 font-mono text-xl font-bold tracking-widest text-primary shadow-sm transition-colors hover:border-primary"
          >
            <span data-testid="report-success-ref">{created.ref}</span>
            <Copy size={20} weight="duotone" />
          </button>
          <div className="mt-10 flex flex-col gap-3">
            <Link
              to={`/suivi/${created.ref}`}
              data-testid="success-track-link"
              className="rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442]"
            >
              Suivre ce signalement
            </Link>
            <Link to="/" data-testid="success-home-link" className="text-sm font-bold text-muted-foreground hover:text-primary">
              Retour à l'accueil
            </Link>
          </div>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-40 md:pb-0" data-testid="report-new-page">
      <Header />
      <main className="mx-auto max-w-2xl px-5 py-8 sm:py-12">
        <div className="mb-8">
          <div className="mb-3 flex items-center justify-between text-xs font-bold text-muted-foreground">
            <span data-testid="report-step-label">Étape {displayStep + 1} sur {stepLabels.length} · {stepLabels[displayStep]}</span>
            <span>{Math.round(((displayStep + 1) / stepLabels.length) * 100)}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-black/5">
            <motion.div
              className="h-full rounded-full bg-primary"
              animate={{ width: `${((displayStep + 1) / stepLabels.length) * 100}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.3 }}
          >
            {step === 0 && (
              <div>
                <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em]">Quel est le problème ?</h1>
                <p className="mt-2 text-sm text-muted-foreground">Choisissez la catégorie la plus proche.</p>
                {building.welcome_message && building.welcome_enabled !== false && (
                  <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-primary/15 bg-primary/[0.04] px-4 py-3" data-testid="report-welcome-message">
                    <Info size={18} weight="duotone" className="mt-0.5 shrink-0 text-primary" />
                    <p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground"><PhoneText text={building.welcome_message} /></p>
                  </div>
                )}
                <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-black/5 bg-white px-4 py-3" data-testid="report-otp-notice">
                  <ShieldCheck size={18} weight="duotone" className="mt-0.5 shrink-0 text-primary" />
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    <strong className="text-foreground">Un contrôle sera effectué avant validation :</strong> un code de vérification SMS vous sera envoyé pour confirmer votre signalement.
                  </p>
                </div>
                <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {orderedCategories.map(({ key, label, Icon }) => {
                    const disabled = key === "ascenseur" && elevatorUnavailable;
                    return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`report-category-${key}`}
                      disabled={disabled}
                      onClick={() => !disabled && pickCategory(key)}
                      className={cn(
                        "flex flex-col items-start gap-3 rounded-3xl border bg-white p-4 text-left transition-all duration-200",
                        disabled && "cursor-not-allowed opacity-40",
                        form.category === key
                          ? "border-primary ring-2 ring-primary/30 shadow-md"
                          : "border-black/5 hover:border-primary/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                      )}
                    >
                      <Icon size={26} weight="duotone" className={form.category === key ? "text-primary" : "text-foreground/60"} />
                      <span className="text-sm font-bold leading-tight">{label}</span>
                    </button>
                    );
                  })}
                </div>
                {renderCategoryNotice()}
                {elevatorUnavailable && (
                  <p className="mt-3 text-xs text-muted-foreground" data-testid="elevator-unavailable-note">
                    Aucun ascenseur n'est déclaré dans les bâtiments de cette résidence.
                  </p>
                )}
              </div>
            )}

            {step === 1 && (
              <div>
                <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em]">Où se situe-t-il ?</h1>
                <p className="mt-2 text-sm text-muted-foreground">Dans les parties communes de la résidence.</p>
                {renderCategoryNotice()}
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {LOCATIONS.map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      data-testid={`location-chip-${loc.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => set("location")(loc)}
                      className={cn(
                        "rounded-full border px-5 py-2.5 text-sm font-bold transition-all duration-200",
                        form.location === loc
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-black/10 bg-white text-foreground/70 hover:border-primary/50"
                      )}
                    >
                      {loc}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={form.location_detail}
                  onChange={(e) => set("location_detail")(e.target.value)}
                  placeholder="Précision (ex. 2e étage, porte B) — optionnel"
                  data-testid="location-detail-input"
                  className="mt-6 w-full rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-sm outline-none transition-colors focus:border-primary"
                />
              </div>
            )}

            {step === 2 && (
              <div>
                <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em]">{isElevator || form.category === "porte_garage" || form.category === "chauffage" ? "Décrivez la panne" : "Décrivez la situation"}</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {isElevator
                    ? "Portes bloquées, cabine coincée, boutons inactifs… dites-nous ce qui ne fonctionne pas."
                    : "Quelques mots suffisent pour comprendre et agir vite."}
                </p>
                {renderCategoryNotice()}
                {issueChips && (
                  <div className="mt-5">
                    <p className="text-sm font-bold">Quel est le problème ? <span className="font-normal text-muted-foreground">(touchez pour sélectionner — ce sera l'objet de la demande)</span></p>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="issue-chips">
                      {issueChips.map((issue) => {
                        const active = form.issue === issue.label;
                        const IssueIcon = issue.Icon;
                        return (
                          <button
                            key={issue.label}
                            type="button"
                            onClick={() => pickIssue(issue)}
                            data-testid={`issue-chip-${issue.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            aria-pressed={active}
                            className={
                              active
                                ? "inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-xs font-bold text-primary-foreground transition-colors"
                                : "inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3.5 py-2 text-xs font-bold transition-colors hover:border-primary hover:text-primary"
                            }
                          >
                            <IssueIcon size={14} weight="duotone" />
                            {issue.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <textarea
                  value={form.description}
                  onChange={(e) => set("description")(e.target.value)}
                  rows={4}
                  placeholder={isElevator ? "Ex. Ascenseur bloqué entre le 2e et le 3e étage, portes fermées…" : "Ex. L'ampoule du palier du 2e étage est grillée depuis hier soir…"}
                  data-testid="report-description-input"
                  className="mt-6 w-full resize-none rounded-2xl border border-black/10 bg-white px-5 py-4 text-sm leading-relaxed outline-none transition-colors focus:border-primary"
                />
                {isElevator && subRows.length > 0 && (
                  <p className="mt-3 text-sm" data-testid="elevator-device-display">
                    Numéros d'appareil :{" "}{form.location_detail ? (
                      <span className="font-bold text-primary">{form.location_detail}</span>
                    ) : (
                      <span className="text-muted-foreground">choisissez le bâtiment ci-dessous</span>
                    )}
                  </p>
                )}
                {isElevator && selectedSub?.prestataire && (
                  <div className="mt-1 text-sm" data-testid="elevator-provider-display">
                    <p>Entretien : <span className="font-bold text-primary"><PhoneText text={selectedSub.prestataire} /></span></p>
                    {ratedOf(selectedSub.prestataire) && <ProviderRating service={ratedOf(selectedSub.prestataire)} testid="elevator-provider-rating" />}
                    <p className="text-muted-foreground">Il sera informé de votre signalement.</p>
                  </div>
                )}
                {isElevator && subRows.length === 0 && (
                  <div className="mt-4">
                    <label htmlFor="elevator-device" className="text-sm font-bold">
                      Numéros d'appareil <span className="font-normal text-[#C90A0A]">(obligatoire)</span>
                    </label>
                    <input
                      id="elevator-device"
                      type="text"
                      required
                      value={form.location_detail}
                      onChange={(e) => set("location_detail")(e.target.value)}
                      placeholder="Numéros d'appareil"
                      data-testid="elevator-device-input"
                      className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-sm outline-none transition-colors focus:border-primary"
                    />
                  </div>
                )}
                {form.category === "chauffage" && hasChauffagiste && (
                  <div className="mt-3 text-sm" data-testid="heating-provider-display">
                    {selectedSub?.chauffagiste ? (
                      <>
                        <p>Chauffagiste : <span className="font-bold text-primary"><PhoneText text={selectedSub.chauffagiste} /></span></p>
                        {ratedOf(selectedSub.chauffagiste) && <ProviderRating service={ratedOf(selectedSub.chauffagiste)} testid="heating-provider-rating" />}
                        <p className="text-muted-foreground">Il sera informé de votre signalement.</p>
                      </>
                    ) : form.sub_building ? (
                      <p className="text-muted-foreground">Chauffagiste : non renseigné pour ce bâtiment</p>
                    ) : (
                      <p className="text-muted-foreground">Chauffagiste : choisissez le bâtiment ci-dessous</p>
                    )}
                  </div>
                )}
                {form.category === "porte_garage" && doorRows.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm" data-testid="garage-door-number">
                      Numéros d'appareil :{" "}
                      {selectedDoor?.number ? (
                        <span className="font-bold text-primary">{selectedDoor.number}</span>
                      ) : (
                        <span className="text-muted-foreground">choisissez la porte ci-dessous</span>
                      )}
                    </p>
                    {selectedDoor?.prestataire && (
                      <div className="mt-1 text-sm" data-testid="garage-provider-display">
                        <p>Entretien : <span className="font-bold text-primary"><PhoneText text={selectedDoor.prestataire} /></span></p>
                        {ratedOf(selectedDoor.prestataire) && <ProviderRating service={ratedOf(selectedDoor.prestataire)} testid="garage-provider-rating" />}
                        <p className="text-muted-foreground">Il sera informé de votre signalement.</p>
                      </div>
                    )}
                    <label htmlFor="report-garage-door" className="mt-1 block text-sm font-bold">
                      Porte concernée <span className="font-normal text-[#C90A0A]">(obligatoire)</span>
                    </label>
                    <select
                      id="report-garage-door"
                      required
                      value={form.garage_door}
                      onChange={(e) => pickDoor(e.target.value)}
                      data-testid="report-garage-door-select"
                      className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-sm outline-none transition-colors focus:border-primary"
                    >
                      <option value="">Choisissez la porte…</option>
                      {doorRows.map((d) => (
                        <option key={d.name} value={d.name}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {providerSpecialty && !noProvider && providerOptions.length > 0 && (
                  <div className="mt-4">
                    {selectedProvider && (
                      <div className="mb-2 text-sm" data-testid="report-provider-display">
                        <p>
                          Prestataire : <span className="font-bold text-primary">{selectedProvider.name}</span>
                          {selectedProvider.phone ? <> — <PhoneText text={selectedProvider.phone} /></> : null}
                        </p>
                        <ProviderRating service={selectedProvider} testid="report-provider-rating" />
                      </div>
                    )}
                    <label htmlFor="report-provider" className="text-sm font-bold">
                      Prestataire à solliciter <span className="font-normal text-muted-foreground">(optionnel)</span>
                    </label>
                    <select
                      id="report-provider"
                      value={form.provider_id}
                      onChange={(e) => set("provider_id")(e.target.value)}
                      data-testid="report-provider-select"
                      className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-sm outline-none transition-colors focus:border-primary"
                    >
                      <option value="">Choisissez un prestataire…</option>
                      {providerOptions.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-muted-foreground">Il sera indiqué sur votre signalement pour le gestionnaire.</p>
                  </div>
                )}
                {building.sub_buildings?.length > 0 && form.category !== "porte_garage" && (
                  <div className="mt-4">
                    <label htmlFor="report-sub-building" className="text-sm font-bold">
                      Bâtiment <span className="font-normal text-[#C90A0A]">(obligatoire)</span>
                    </label>
                    <select
                      id="report-sub-building"
                      required
                      value={form.sub_building}
                      onChange={(e) => pickSubBuilding(e.target.value)}
                      data-testid="report-sub-building-select"
                      className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-5 py-3.5 text-sm outline-none transition-colors focus:border-primary"
                    >
                      <option value="">Choisissez votre bâtiment…</option>
                      {subOptions.map((sb) => (
                        <option key={sb.name} value={sb.name}>{sb.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="mt-6">
                  <PhotoUploader photos={form.photos} onChange={set("photos")} testidPrefix="report-photo" />
                </div>
                {urgencyEnabled && (
                  <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-black/10 bg-white px-5 py-3.5" data-testid="report-urgent-row">
                  <p className="text-sm font-bold">
                    C'est urgent
                    <span className="mt-0.5 block text-xs font-normal text-muted-foreground">Fuite, personne coincée… le prestataire verra un bandeau rouge URGENCE.</span>
                  </p>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={urgentOn}
                    onClick={() => set("urgent")(!urgentOn)}
                    data-testid="report-urgent-toggle"
                    className={cn(
                      "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200",
                      urgentOn ? "bg-[#C8102E]" : "bg-black/15"
                    )}
                  >
                    <span
                      className={cn(
                        "absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] duration-200",
                        urgentOn ? "left-6" : "left-1"
                      )}
                    />
                  </button>
                </div>
                )}
                <div className="mt-8 rounded-3xl border border-black/5 bg-white p-5">
                  <p className="text-sm font-bold">Vos coordonnées <span className="font-normal text-[#C90A0A]">(toutes obligatoires)</span></p>
                  <p className="mt-1 text-xs text-muted-foreground">Un code de validation vous sera envoyé par email pour confirmer l'envoi du signalement.</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <select
                      required
                      value={form.contact_civility}
                      onChange={(e) => set("contact_civility")(e.target.value)}
                      data-testid="contact-civility-select"
                      aria-label="Civilité"
                      className="rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                    >
                      <option value="">Civilité *</option>
                      <option value="M.">M.</option>
                      <option value="Mme">Mme</option>
                    </select>
                    <input
                      type="text"
                      required
                      value={form.contact_first_name}
                      onChange={(e) => set("contact_first_name")(e.target.value)}
                      placeholder="Prénom *"
                      data-testid="contact-firstname-input"
                      className="rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                    />
                    <input
                      type="text"
                      required
                      value={form.contact_last_name}
                      onChange={(e) => set("contact_last_name")(e.target.value)}
                      placeholder="Nom *"
                      data-testid="contact-lastname-input"
                      className="rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                    />
                    <input
                      type="tel"
                      required
                      value={form.contact_phone}
                      onChange={(e) => set("contact_phone")(e.target.value)}
                      placeholder="Téléphone *"
                      data-testid="contact-phone-input"
                      className="rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                    />
                    <input
                      type="email"
                      required
                      value={form.contact_email}
                      onChange={(e) => set("contact_email")(e.target.value)}
                      placeholder="Email *"
                      data-testid="contact-email-input"
                      className="rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary sm:col-span-2"
                    />
                    <label className="flex cursor-pointer items-start gap-2.5 rounded-2xl border border-black/10 px-4 py-3 transition-colors hover:border-primary/40 sm:col-span-2" data-testid="notify-resolved-label">
                      <input
                        type="checkbox"
                        checked={form.notify_resolved}
                        onChange={(e) => set("notify_resolved")(e.target.checked)}
                        data-testid="notify-resolved-checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      />
                      <span className="text-xs leading-relaxed text-muted-foreground">
                        Me prévenir par email de l'avancement, notamment quand le problème sera <strong className="text-foreground">résolu</strong>.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em]">On vérifie ensemble ?</h1>
                <div className="mt-8 space-y-4 rounded-3xl border border-black/5 bg-white p-6" data-testid="report-summary">
                  {[
                    ["Catégorie", categoryByKey(form.category).label],
                    ...(form.issue ? [["Problème", form.issue]] : []),
                    ...(urgentOn ? [["Urgence", "Oui — bandeau rouge pour le prestataire"]] : []),
                    ["Lieu", form.location + (form.location_detail ? ` — ${form.location_detail}` : "")],
                    ...(form.sub_building ? [["Bâtiment", form.sub_building]] : []),
                    ...(selectedProvider
                      ? [["Prestataire", selectedProvider.name + (selectedProvider.phone ? ` — ${selectedProvider.phone}` : "")]]
                      : []),
                    ["Description", form.description],
                    ["Photos", form.photos.length ? `${form.photos.length} photo(s)` : "Aucune"],
                    ["Contact", [`${form.contact_civility} ${form.contact_first_name} ${form.contact_last_name}`.trim(), form.contact_phone, form.contact_email].filter(Boolean).join(" · ")],
                  ].map(([k, v]) => (
                    <div key={k} className="flex gap-4 text-sm">
                      <span className="w-28 shrink-0 font-bold text-muted-foreground">{k}</span>
                      <span className="font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-black/5 bg-white/90 backdrop-blur-xl md:bottom-0">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-5 py-4">
          {step > 0 ? (
            <button
              type="button"
              onClick={goBack}
              data-testid="report-back-button"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 px-6 py-3 text-sm font-bold transition-colors hover:border-primary hover:text-primary"
            >
              <ArrowLeft size={16} weight="bold" /> Retour
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="report-cancel-button"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 px-6 py-3 text-sm font-bold transition-colors hover:border-primary hover:text-primary"
            >
              <ArrowLeft size={16} weight="bold" /> Annuler
            </button>
          )}
          {step < 3 ? (
            <button
              type="button"
              disabled={!canNext}
              onClick={goNext}
              data-testid="report-next-button"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-bold text-primary-foreground transition-all duration-200 hover:bg-[#265442] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Continuer <ArrowRight size={16} weight="bold" />
            </button>
          ) : (
            <button
              type="button"
              disabled={sending}
              onClick={submit}
              data-testid="report-submit-button"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-bold text-primary-foreground transition-all duration-200 hover:bg-[#265442] disabled:opacity-60"
            >
              {sending ? <SpinnerGap size={18} className="animate-spin" /> : null}
              Envoyer le signalement
            </button>
          )}
        </div>
      </div>
      <div className="hidden md:block md:h-8" />
    </div>
  );
}
